import * as THREE from "three/webgpu";
import {SketchRelationContainer} from '../../sketch/solver/constraint/SketchRelationContainer';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {
    EllipseControlPointRole,
    ISketchObject,
    ParabolaControlPointRole,
    SketchPointBoxSelectionRole,
    SketchPointKind,
    type SketchRelatedObject,
    SlotControlPointRole,
} from './ISketchObject';
import {isSketchObject} from '../guards/isSketchObject';
import type {AbstractSketchEdgeObject} from './AbstractSketchEdgeObject';
import {SketchEntityKind} from '../../sketch/solver/constraint/relation-compatibility/SketchEntityKinds';
import {SketchRelationKind} from '../../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';
import {SketchSnapshot} from '../meshProxy/SketchSnapshot';
import type {SketchCloneContext} from './SketchCloneContext';
import {cloneSingleTargetConstraints} from './SketchConstraintClone';
import {SketchAuxiliaryObjectState} from './SketchAuxiliaryObjectState';
import {markOwnerSketchGroupDirty} from './utils/markOwnerSketchGroupDirty';
import {createSketchEntityPersistentId} from '../../sketch/identity/SketchPersistentIdFactory';
import {PointsMaterialFactory, SketchPointCloneMaterialFallbackKind,} from '../../material/PointsMaterialFactory';
import {resolveObject3DSceneAncestor} from '../../utils/scene-object/resolveObject3DSceneAncestor';
import {SKETCH_CONTROL_POINT_DEFAULT_SCALE} from '../../constants/sketch-point-visual';

/** 点对象默认显示缩放值。 */
const SKETCH_POINT_DEFAULT_SCALE = 0.015;
/** 控制点交互态（移入/选中）显示缩放值。 */
const CONTROL_POINT_INTERACTION_SCALE = 0.009;
/** 草图点创建态材质名称（与 PointsMaterialFactory 中的创建态名称保持一致）。 */
const SKETCH_POINT_CREATED_MATERIAL_NAME = '草图点-创建态材质';


/**
 * {@link SketchPointObject} 构造参数
 */
export interface SketchPointObjectOptions {
    /** 世界坐标系下的初始点位*/
    position: THREE.Vector3;
    /** 点对象类型（控制草图点）*/
    pointKind?: SketchPointKind;
    /** 点对象框选角色。 */
    boxSelectionRole?: SketchPointBoxSelectionRole;
    /** 可选渲染材质*/
    material: THREE.SpriteNodeMaterial;
    /** 创建时所属抽象参考面*/
    referencePlane: ReferencePlane;
    /** 创建该点时捕捉到的来源点；仅记录引用关系，不表示拓扑共点。 */
    sourcePoint?: SketchPointObject | null;
    /** 是否为辅助对象。 */
    isAuxiliaryObject?: boolean;
}

/**
 * 草图点网格对象
 * 职责
 * 1. 使用 THREE.Sprite 表达一个拓扑点
 * 2. 暴露统一草图对象接口与约束状态；
 * 3. 点位变化后通知关联线刷新几何；
 * 4. 通过 pointKind 区分控制点与草图点
 */
export class SketchPointObject extends THREE.Sprite implements ISketchObject {
    /** 草图点创建态默认缩放值。 */
    private static readonly SKETCH_POINT_CREATED_DEFAULT_SCALE: number = SKETCH_CONTROL_POINT_DEFAULT_SCALE;

    isSketchObject: boolean = true
    /** 点对象不是封闭几何。 */
    public readonly isClosedGeometry: boolean = false;

    /** 跨会话稳定持久 ID；`cloneSelf` 继承，复制出新实体的流程必须重新生成。 */
    public persistentId: string = createSketchEntityPersistentId();

    /** 对象运行时类型标记*/
    public readonly objectType = SketchEntityKind.Point;

    public readonly isSketchPointObject: boolean = true;

    /** 点对象类型*/
    public readonly pointKind: SketchPointKind;


    /**
     * 点对象框选角色。
     * 说明：
     * 1. 普通控制点默认不参与框选，避免矩形角点、样条点等控制点被框选误选；
     * 2. 矩形构造线交点与倒角原交点焦点具有独立选择语义，专用角色还用于拖拽归属和旧数据兼容；
     * 3. 使用领域字段而非 `userData`，保证复制、历史恢复和选择过滤读取同一份语义。
     */
    public boxSelectionRole: SketchPointBoxSelectionRole;


    /** 创建时所属参考面*/
    public readonly referencePlane: ReferencePlane;

    /** 当前点对象的约束容器*/
    public readonly constraints: SketchRelationContainer = new SketchRelationContainer(this);

    /** 点对象拖动留影代理。 */
    public readonly dragSnapshotProxy: SketchSnapshot = new SketchSnapshot();

    /** 辅助对象状态内部存储。 */
    private _auxiliaryObjectState: SketchAuxiliaryObjectState = SketchAuxiliaryObjectState.Normal;

    /**
     * 获取当前点对象是否为辅助对象。
     * @returns 若为辅助对象返回 true，否则返回 false。
     */
    public get isAuxiliaryObject(): boolean {
        return this._auxiliaryObjectState === SketchAuxiliaryObjectState.Auxiliary;
    }

    /**
     * 设置当前点对象是否为辅助对象。
     * @param value 新的辅助对象标记。
     */
    public set isAuxiliaryObject(value: boolean) {
        this._auxiliaryObjectState = value
            ? SketchAuxiliaryObjectState.Auxiliary
            : SketchAuxiliaryObjectState.Normal;
    }


    /**
     * 椭圆控制点角色。
     * 说明：
     * 1. 为 null 时表示该点不是椭圆的基准轴控制点；
     * 2. 拖拽过程中通过此属性判断是否需要触发椭圆重建。
     */
    private _ellipseControlPointRole: EllipseControlPointRole | null = null;

    /**
     * 获取椭圆控制点角色。
     * @returns 椭圆控制点角色；非椭圆控制点返回 null。
     */
    public get ellipseControlPointRole(): EllipseControlPointRole | null {
        return this._ellipseControlPointRole;
    }

    /**
     * 设置椭圆控制点角色。
     * @param role 椭圆控制点角色；传入 null 清除标记。
     */
    public set ellipseControlPointRole(role: EllipseControlPointRole | null) {
        this._ellipseControlPointRole = role;
    }


    /**
     * 槽口控制点角色。
     * 说明：
     * 1. 为 null 时表示该点不是槽口的控制点；
     * 2. 拖拽过程中通过此属性判断是否需要触发槽口几何重建。
     */
    private _slotControlPointRole: SlotControlPointRole | null = null;

    /**
     * 获取槽口控制点角色。
     * @returns 槽口控制点角色；非槽口控制点返回 null。
     */
    public get slotControlPointRole(): SlotControlPointRole | null {
        return this._slotControlPointRole;
    }

    /**
     * 设置槽口控制点角色。
     * @param role 槽口控制点角色；传入 null 清除标记。
     */
    public set slotControlPointRole(role: SlotControlPointRole | null) {
        this._slotControlPointRole = role;
    }


    /**
     * 抛物线控制点角色。
     * 说明：
     * 1. 为 null 时表示该点不是抛物线控制点；
     * 2. 拖拽过程中通过此属性判断是否需要触发抛物线定义重建。
     */
    private _parabolaControlPointRole: ParabolaControlPointRole | null = null;

    /**
     * 获取抛物线控制点角色。
     * @returns 抛物线控制点角色；非抛物线控制点返回 null。
     */
    public get parabolaControlPointRole(): ParabolaControlPointRole | null {
        return this._parabolaControlPointRole;
    }

    /**
     * 设置抛物线控制点角色。
     * @param role 抛物线控制点角色；传入 null 清除标记。
     */
    public set parabolaControlPointRole(role: ParabolaControlPointRole | null) {
        this._parabolaControlPointRole = role;
    }


    /**
     * 与当前点相连、需要联动刷新的线状实体集合（线段、圆等均包含在内）
     */
    private readonly connectedEdges = new Set<AbstractSketchEdgeObject>();

    /** 与当前点相关联的草图对象集合。 */
    private readonly relatedSketchObjects = new Set<SketchRelatedObject>();


    /**
     * 当前点的创建来源点。
     * 说明：
     * 1. 该引用只表达“由捕捉这个点创建”，不表达拓扑共点；
     * 2. 来源点可能被多个新建点引用，因此反向关系使用集合维护；
     * 3. 不使用 Object3D 父子关系，避免影响场景层级与选择逻辑。
     */
    private sourcePoint: SketchPointObject | null = null;

    /** 以当前点作为来源创建出的点集合。 */
    private readonly derivedPoints = new Set<SketchPointObject>();


    /** 是否为控制点。 */
    public isControlPoint: boolean = false;

    /** 是否为端点。 */
    public isEndPoint: boolean = false;

    /** 是否为中心点。 */
    public isCenterPoint: boolean = false;

    /** 是否为圆、圆弧或椭圆的中心十字控制点。 */
    public isCurveCenterControlPoint: boolean = false;

    /** 是否为中点。 */
    public isMidPoint: boolean = false;

    /** 是否为象限点。 */
    public isQuadrantPoint: boolean = false;


    /**
     * 当前点作为边端点被占用的引用计数。
     * 说明：
     * 1. 一个点对象可能被多条边共享为端点；
     * 2. 仅当计数归零时，才允许回退 `isEndPoint` 角色；
     * 3. 配合中点角色可实现“复用为端点显示、解绑后恢复隐藏”的可逆行为。
     */
    private endpointRoleReferenceCount: number = 0;


    /**
     * 是否为绘制过程中跟随鼠标移动的临时预览点。
     * 说明：
     * 1. 该标记仅用于区分“正式草图点”和“绘制态预览点”；
     * 2. 为 true 时，选择管理器不应将其纳入 hover / click 选择候选；
     * 3. 默认值为 false，正式进入草图模型的点对象必须保持 false。
     */
    public isDrawingPreviewPoint: boolean = false;


    /**
     * 是否为草图会话原点投影。
     * 说明：
     * 1. 该标记仅用于绘制期临时投影点；
     * 2. 交互状态系统据此应用专用红色材质，而不是普通草图点默认 SVG；
     * 3. 默认值为 false，正式草图点不得开启此标记。
     */
    public isDrawingSketchOrigin: boolean = false;


    /**
     * 是否为特征面边界临时捕捉点。
     * 说明：
     * 1. 该标记仅用于“选择特征面作为草图平面”后的边角点/边中点投影；
     * 2. 这些点与草图会话原点投影一样只提供捕捉参考，不进入正式草图数据；
     * 3. 单独标记可避免把边界参考点误判为草图原点。
     */
    public isSketchSessionReferencePoint: boolean = false;

    /** 是否为鼠标悬停可见模型边后动态生成的真实拓扑端点投影。 */
    public isSketchSessionModelEdgeEndpoint: boolean = false;

    /** 是否为鼠标悬停可见模型边后动态生成的真实曲线中点投影。 */
    public isSketchSessionModelEdgeMidpoint: boolean = false;

    /** 是否为可见模型圆、圆弧或椭圆唤醒后动态生成的中心投影。 */
    public isSketchSessionModelEdgeCenter: boolean = false;

    /** 是否为鼠标悬停可见模型圆锥边后动态生成的有效象限点投影。 */
    public isSketchSessionModelEdgeQuadrant: boolean = false;


    /**
     * 是否参与象限点捕捉。
     * 说明：圆/椭圆象限点始终为 true；圆弧象限点仅在其角度落在弧范围内时为 true。
     * 与 visible 分离，避免"是否渲染"和"是否可捕捉"混用。
     */
    public isActiveForSnap: boolean = true;

    /**
     * 当前一次求解流程中的目标点位
     */
    /**
     * 点拖拽留影嵌套深度。
     *
     * 设计说明：
     * 1. 同一个点可能被点拖拽、样条拖拽、集合拖拽等多条上层链路复用；
     * 2. 使用深度计数可避免重复 show / hide 造成留影闪烁；
     * 3. 关联边只在 0 -> 1 与 1 -> 0 两个边界时接收生命周期通知。
     */
    private dragPreviewDepth: number = 0;

    static count = 0

    public get baseDisplayName(): string {
        return '点';
    }

    /**
     * 构造草图点对象
     * @param options 构造参数
     */
    public constructor(options: SketchPointObjectOptions) {
        const material = options.material
        super(material);

        this.position.set(options.position.x, options.position.y, options.position.z);
        // 默认归类为草图点，可按需创建控制点
        this.pointKind = options.pointKind ?? SketchPointKind.SketchPoint;

        // 框选角色随点对象初始化，默认角色保持既有选择行为不变。
        this.boxSelectionRole = options.boxSelectionRole ?? SketchPointBoxSelectionRole.Default;

        this.isControlPoint = this.pointKind === SketchPointKind.ControlPoint;
        // 统一初始化点对象默认缩放，避免构造阶段出现交互态残留。
        this.restoreDefaultScale();
        this.referencePlane = options.referencePlane;

        // 在构造阶段写入辅助对象状态，确保后续交互逻辑可直接读取。
        this.isAuxiliaryObject = options.isAuxiliaryObject ?? false;


        // 构造阶段绑定来源点，确保反向一对多引用同步建立。
        this.bindSourcePoint(options.sourcePoint ?? null);

        this.name = `${this.baseDisplayName}${SketchPointObject.count}`;
        SketchPointObject.count += 1

        this.renderOrder = 11
    }


    /**
     * 复制当前点对象，返回与源对象无共享可变引用的新实例。
     * 说明：
     * 1. 当传入复制上下文时，会按旧点 UUID 复用同一个副本，保证复合对象内部共享点关系；
     * 2. 新点不会继承任何旧对象的关联边/关联草图对象集合；
     * 3. 点位、角色标记、显示状态会完整复制；
     * 4. 捕捉来源引用属于运行时创建链路，不在普通复制中跨对象继承，避免副本指向原草图点。
     * @param context 复制上下文。
     * @returns 当前点对象的独立副本。
     * @throws 当源点的材质不是 `THREE.SpriteNodeMaterial` 时抛出错误。
     */
    public cloneSelf(context?: SketchCloneContext): SketchPointObject {
        const contextClonedPoint = context?.pointMap.get(this.uuid);
        if (contextClonedPoint) {
            if (context && !context.objectMap.has(this.uuid)) {
                context.objectMap.set(this.uuid, contextClonedPoint);
            }
            return contextClonedPoint;
        }

        const sourceMaterial = Array.isArray(this.material)
            ? this.material[0]
            : this.material;

        if (!(sourceMaterial instanceof THREE.SpriteNodeMaterial)) {
            throw new Error('点对象材质类型非法，无法复制。');
        }

        const clonedPoint = new SketchPointObject({
            position: this.getPosition(),
            pointKind: this.pointKind,
            // TSL 节点材质直接 clone 会丢失圆形遮罩和固定屏幕尺寸配置，复制时必须回到材质工厂规范实例。
            material: context?.reusePointMaterial
                ? sourceMaterial
                : PointsMaterialFactory.resolveSketchPointCloneMaterial(
                    sourceMaterial.name,
                    this.resolveCloneMaterialFallbackKind(),
                ),
            referencePlane: this.referencePlane,
        });

        if (context) {
            context.pointMap.set(this.uuid, clonedPoint);
            context.objectMap.set(this.uuid, clonedPoint);
        }

        // 副本表达同一逻辑点，必须继承持久身份；复制出新实体的流程负责显式重新生成。
        clonedPoint.persistentId = this.persistentId;
        // 复制点角色与捕捉标记，确保交互行为一致。
        clonedPoint.isControlPoint = this.isControlPoint;
        clonedPoint.isEndPoint = this.isEndPoint;
        clonedPoint.isCenterPoint = this.isCenterPoint;
        clonedPoint.isCurveCenterControlPoint = this.isCurveCenterControlPoint;
        clonedPoint.isMidPoint = this.isMidPoint;
        clonedPoint.isQuadrantPoint = this.isQuadrantPoint;

        // 复制框选角色，确保撤销/重做和对象复制后专用交点仍可通过框选命中。
        clonedPoint.boxSelectionRole = this.boxSelectionRole;

        clonedPoint.isActiveForSnap = this.isActiveForSnap;
        clonedPoint.ellipseControlPointRole = this.ellipseControlPointRole;
        clonedPoint.slotControlPointRole = this.slotControlPointRole;
        clonedPoint.parabolaControlPointRole = this.parabolaControlPointRole;

        // 复制辅助对象标记，确保复制结果保持同样的事件语义。
        clonedPoint.isAuxiliaryObject = this.isAuxiliaryObject;


        // 复制绘制态预览点标记，保证复制结果与源对象交互语义一致。
        clonedPoint.isDrawingPreviewPoint = this.isDrawingPreviewPoint;


        // 复制草图会话原点投影标记，确保特殊材质分支在副本上仍然有效。
        clonedPoint.isDrawingSketchOrigin = this.isDrawingSketchOrigin;


        // 复制临时参考点标记，保证副本仍走同一套红色参考点视觉规则。
        clonedPoint.isSketchSessionReferencePoint = this.isSketchSessionReferencePoint;
        clonedPoint.isSketchSessionModelEdgeEndpoint = this.isSketchSessionModelEdgeEndpoint;
        clonedPoint.isSketchSessionModelEdgeMidpoint = this.isSketchSessionModelEdgeMidpoint;
        clonedPoint.isSketchSessionModelEdgeCenter = this.isSketchSessionModelEdgeCenter;
        clonedPoint.isSketchSessionModelEdgeQuadrant = this.isSketchSessionModelEdgeQuadrant;


        // 复制显示状态，保证视觉结果与源对象一致。
        clonedPoint.position.copy(this.position);
        clonedPoint.quaternion.copy(this.quaternion);
        clonedPoint.scale.copy(this.scale);
        clonedPoint.visible = this.visible;
        clonedPoint.renderOrder = this.renderOrder;
        clonedPoint.frustumCulled = this.frustumCulled;
        clonedPoint.layers.mask = this.layers.mask;

        // 复制点对象的单目标几何关系（如固定约束）。
        cloneSingleTargetConstraints(this, clonedPoint, context?.shouldCloneConstraint);


        return clonedPoint;
    }


    /**
     * 解析当前点复制材质兜底类型。
     *
     * 设计说明：
     * 1. 正常数据会通过材质名称精确恢复规范材质；
     * 2. 旧数据或临时材质可能没有规范名称，此时仍要按点类型保证复制结果可见且尺寸正确；
     * 3. 该方法只做点类型到材质兜底类型的映射，不直接创建材质，保持点对象职责单一。
     *
     * @returns 当前点对应的复制材质兜底类型。
     */
    private resolveCloneMaterialFallbackKind(): SketchPointCloneMaterialFallbackKind {
        switch (this.pointKind) {
            case SketchPointKind.ControlPoint:
                return SketchPointCloneMaterialFallbackKind.ControlPoint;
            case SketchPointKind.SketchPoint:
                return SketchPointCloneMaterialFallbackKind.SketchPoint;
            default:
                throw new Error(`未支持的草图点类型（复制材质）：${this.pointKind as string}`);
        }
    }


    /**
     * 绑定创建来源点，并同步来源点的一对多反向引用集合。
     * 说明：
     * 1. 传入 null 表示解除来源引用；
     * 2. 禁止点引用自身，避免形成无意义自环；
     * 3. 若重复绑定同一个来源点则直接返回，避免集合重复写入。
     * @param sourcePoint 创建当前点时捕捉到的来源点。
     */
    public bindSourcePoint(sourcePoint: SketchPointObject | null): void {
        if (sourcePoint === this) {
            return;
        }
        if (this.sourcePoint === sourcePoint) {
            return;
        }
        if (this.sourcePoint) {
            this.sourcePoint.derivedPoints.delete(this);
        }
        this.sourcePoint = sourcePoint;
        if (sourcePoint) {
            sourcePoint.derivedPoints.add(this);
        }
    }

    /**
     * 解除当前点的来源点引用。
     */
    public clearSourcePoint(): void {
        this.bindSourcePoint(null);
    }

    /**
     * 获取当前点的创建来源点。
     * @returns 来源点；没有来源时返回 null。
     */
    public getSourcePoint(): SketchPointObject | null {
        return this.sourcePoint;
    }

    /**
     * 获取由当前点派生创建出的点对象快照。
     * @returns 派生点数组快照。
     */
    public getDerivedPointsSnapshot(): readonly SketchPointObject[] {
        return [...this.derivedPoints];
    }


    /**
     * 应用移入/选中交互态的点显示缩放。
     * 说明：
     * 1. 普通草图点的交互反馈只改变材质颜色，必须保持当前雪花尺寸不变；
     * 2. 控制点、草图会话原点投影和模型投影参考点继续使用原有交互放大反馈；
     * 3. 不修改点几何位置，也不触发任何约束求解或拓扑联动。
     */
    public applyHoverOrSelectedScale(): void {
        if (this.isEllipseAxisControlPoint()) {
            // 椭圆轴端点始终与普通草图点保持相同雪花尺寸，按下或移入只改变颜色，不能缩小成控制点尺寸。
            return;
        }
        const isOrdinarySketchPoint = this.pointKind === SketchPointKind.SketchPoint
            && !this.isDrawingSketchOrigin
            && !this.isSketchSessionReferencePoint
            && !this.isMidPoint
            && !this.isQuadrantPoint;
        if (isOrdinarySketchPoint) {
            // 普通草图点默认态、移入态和选中态共用雪花形状；中点和象限辅助点仍由既有辅助点规则控制尺寸。
            return;
        }

        const interactionScale = this.resolveInteractionScaleByPointKind();
        // 控制点和特殊参考点保留既有放大反馈，避免改变其他点类型的交互语义。
        this.scale.set(
            interactionScale,
            interactionScale,
            interactionScale,
        );
    }

    /**
     * 恢复点对象默认显示缩放。
     * 说明：
     * 1. 用于交互态结束后回退到基础显示大小；
     * 2. 仅修改渲染缩放，不影响点位与约束。
     */
    public restoreDefaultScale(): void {
        const defaultScale = this.resolveDefaultScaleByPointKind();
        // 默认态按点类型恢复对应缩放，保证草图点与控制点视觉区分。
        this.scale.set(
            defaultScale,
            defaultScale,
            defaultScale,
        );
    }

    /**
     * 按点类型解析默认态缩放值。
     * @returns 默认态缩放值。
     */
    private resolveDefaultScaleByPointKind(): number {
        if (this.isDrawingSketchOrigin || this.isSketchSessionReferencePoint) {
            // 草图临时参考点按控制点默认大小显示，避免比普通控制点更大。
            return SKETCH_CONTROL_POINT_DEFAULT_SCALE;
        }
        if (this.isEllipseAxisControlPoint()) {
            // 椭圆长短轴端点采用草图点雪花外观，其默认尺寸也必须与普通草图点完全一致。
            return SKETCH_POINT_DEFAULT_SCALE;
        }
        switch (this.pointKind) {
            case SketchPointKind.SketchPoint:
                /**
                 * 草图点在创建态材质下，默认缩放按控制点默认缩放处理。
                 * 这样可以保证创建态点与控制点的视觉尺寸一致。
                 */
                if (this.isUsingSketchPointCreatedMaterial()) {
                    return SketchPointObject.SKETCH_POINT_CREATED_DEFAULT_SCALE;
                }
                return SKETCH_POINT_DEFAULT_SCALE;
            case SketchPointKind.ControlPoint:
                return SKETCH_CONTROL_POINT_DEFAULT_SCALE;
            default:
                throw new Error(`未支持的点类型（默认缩放）：${this.pointKind as string}`);
        }
    }

    /**
     * 按点类型解析交互态缩放值。
     * @returns 交互态缩放值。
     */
    private resolveInteractionScaleByPointKind(): number {
        if (this.isDrawingSketchOrigin || this.isSketchSessionReferencePoint) {
            // 草图临时参考点在交互态也与控制点保持同样大小规则。
            return CONTROL_POINT_INTERACTION_SCALE;
        }
        switch (this.pointKind) {
            case SketchPointKind.SketchPoint:
                // 普通草图点已在调用入口返回；能够到达这里的是中点或象限辅助点，沿用辅助点交互尺寸。
                return CONTROL_POINT_INTERACTION_SCALE;
            case SketchPointKind.ControlPoint:
                return CONTROL_POINT_INTERACTION_SCALE;
            default:
                throw new Error(`未支持的点类型（交互缩放）：${this.pointKind as string}`);
        }
    }

    /**
     * 判断当前点对象是否正在使用“草图点创建态材质”。
     * @returns 使用创建态材质返回 true，否则返回 false。
     */
    private isUsingSketchPointCreatedMaterial(): boolean {
        return this.material.name === SKETCH_POINT_CREATED_MATERIAL_NAME;
    }


    /**
     * 从几何数据读取当前点位
     * @returns 点位向量副本
     */
    public getPosition(): THREE.Vector3 {
        return this.position.clone()
    }

    /**
     * 把当前点位复制到调用方提供的向量。
     *
     * 高频几何变换使用该入口复用临时向量，避免每个控制点都由 `getPosition()` 分配副本。
     * @param output 承载当前点位的可复用向量。
     * @returns `output` 本身。
     */
    public getPositionTo(output: THREE.Vector3): THREE.Vector3 {
        return output.copy(this.position);
    }

    /**
     * 设置点位并触发派生几何刷新。
     *
     * 该对象方法不再收集或求解约束岛。用户操作必须先通过统一参数协调器获得最终点位；
     * 绘制预览、复制新对象和内部结构写回可直接调用本方法完成单点落位。
     *
     * @param position 新点位。
     */
    public setPosition(position: THREE.Vector3): void {
        const currentPosition = this.getPosition();
        if (currentPosition.equals(position)) {
            return;
        }

        this.writePositionToGeometry(position);
        /**
         * 点位是所有草图几何的最底层定义来源。真实写入后必须标记所属草图为 dirty，
         * 让依赖特征在后续提交时进入稳定重建链路。
         */
        markOwnerSketchGroupDirty(this, {
            reason: 'point-position-changed',
            sourceObjectId: this.uuid,
        });

        // 这里只刷新依赖几何；关系与尺寸必须已经由外层参数协调器求解完成。
        this.notifyConnectedLines();
    }


    /**
     * 拖动开始时进入点级留影生命周期。
     *
     * 设计说明：
     * 1. 场景挂载只决定当前点能否创建视觉快照，不决定关联边是否进入拖拽交互态；
     * 2. 样条异步重建依赖点级开始/结束通知，即使单元对象尚未挂载场景，也必须保持通知对称；
     * 3. 圆的内部半径定义点等未挂载点只发送生命周期通知，不创建留影，因此不会再次触发场景解析异常。
     */
    public beginDragPreview(): void {
        this.dragPreviewDepth += 1;
        if (this.dragPreviewDepth > 1) {
            return;
        }

        // 只有已经进入场景树的点才具备可显示留影；几何生命周期不受挂载状态影响。
        if (resolveObject3DSceneAncestor(this)) {
            this.dragSnapshotProxy.show(this);
        }
        this.connectedEdges.forEach((edge) => edge.onRelatedPointDragPreviewBegan());
    }

    /**
     * 拖动结束时隐藏留影。
     */
    public endDragPreview(): void {
        if (this.dragPreviewDepth <= 0) {
            return;
        }
        this.dragPreviewDepth -= 1;
        if (this.dragPreviewDepth > 0) {
            return;
        }
        this.dragSnapshotProxy.hide();
        this.connectedEdges.forEach((edge) => edge.onRelatedPointDragPreviewEnded());
    }

    /**
     * 获取当前点是否处于拖拽留影态。
     * @returns 若留影深度大于 0 则返回 true。
     */
    public isDragPreviewActive(): boolean {
        return this.dragPreviewDepth > 0;
    }

    /**
     * 静默同步点位到几何。
     * 说明：
     * 1. 仅更新点对象自身几何；
     * 2. 不触发约束求解，也不通知关联边刷新；
     * 3. 仅用于边对象内部回写控制点显示状态，避免形成递归刷新闭环。
     * @param position 目标点位。
     */
    public syncPositionSilently(position: THREE.Vector3): void {
        const currentPosition = this.getPosition();
        if (currentPosition.equals(position)) {
            return;
        }
        this.writePositionToGeometry(position);
    }

    /**
     * 获取点在参考面局部坐标系中的二维位置
     * @returns 局部二维坐标
     */
    public getLocalPosition2D(): THREE.Vector2 {
        return this.referencePlane.worldToLocal(this.getPosition());
    }

    /**
     * 快速获取点在参考面局部坐标系中的 X 坐标
     * @returns 局X 坐标
     */
    get x(): number {
        return this.getLocalPosition2D().x;
    }

    /**
     * 快速获取点在参考面局部坐标系中的 Y 坐标
     * @returns 局Y 坐标
     */
    get y(): number {
        return this.getLocalPosition2D().y;
    }

    /**
     * 按参考面局部二维坐标设置点位置
     * @param localPosition 局部二维坐标
     */
    public setLocalPosition2D(localPosition: THREE.Vector2): void {
        const nextWorld = this.referencePlane.localToWorld(localPosition);
        this.setPosition(nextWorld);
    }

    /**
     * 按参考面局部坐标轴更新点位置
     * @param axis 局部坐标轴
     * @param value 新坐标值
     */
    public setCoordinateOnReferencePlane(
        axis: 'x' | 'y',
        value: number,
    ): void {
        const nextLocal = this.getLocalPosition2D();
        if (axis === 'x') {
            nextLocal.x = value;
        } else {
            nextLocal.y = value;
        }
        this.setLocalPosition2D(nextLocal);
    }

    /**
     * 判定当前点是否处于固定态
     * @returns 固定态判定结果
     */
    public isFixed(): boolean {
        return this.constraints.hasType(SketchRelationKind.Fix);
    }

    /**
     * 判断当前点对象是否允许拖动。
     * 说明：
     * 1. 若点对象自身存在固定约束，则禁止拖动；
     * 2. 若点对象关联的草图对象存在固定约束，则同样禁止拖动；
     * 3. 若关联对象为集合对象，还会继续检查其 `collectSet` 内各源对象的固定约束；
     * 4. 当前方法只做布尔判定，不修改任何状态。
     * @returns 允许拖动返回 true，否则返回 false。
     */
    public canDrag(): boolean {
        if (this.isFixed()) {
            return false;
        }

        for (const relatedObject of this.getRelatedSketchObjectsSnapshot()) {
            if (isSketchObject(relatedObject)) {
                if (
                    relatedObject.constraints.hasType(SketchRelationKind.Fix)
                    || relatedObject.constraints.hasType(SketchRelationKind.FixSlot)
                ) {
                    return false;
                }
                continue;
            }

            if (!('collectSet' in relatedObject) || !(relatedObject.collectSet instanceof Set)) {
                continue;
            }

            for (const collectSource of relatedObject.collectSet) {
                if (!isSketchObject(collectSource)) {
                    continue;
                }
                if (
                    collectSource.constraints.hasType(SketchRelationKind.Fix)
                    || collectSource.constraints.hasType(SketchRelationKind.FixSlot)
                ) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * 注册一条关联边（线段、圆等线状实体均可注册）
     * @param edge 目标线状实体对象
     */
    public registerConnectedEdge(edge: AbstractSketchEdgeObject): void {
        this.connectedEdges.add(edge);
        this.relatedSketchObjects.add(edge);
    }

    /**
     * 解除一条关联边的注册
     * @param edge 目标线状实体对象
     */
    public unregisterConnectedEdge(edge: AbstractSketchEdgeObject): void {
        this.connectedEdges.delete(edge);
        this.relatedSketchObjects.delete(edge);
    }


    /**
     * 绑定端点角色并同步端点显示状态。
     * 说明：
     * 1. 端点角色允许多个边共享，因此使用引用计数；
     * 2. 一旦点承担端点语义，必须强制显示；
     * 3. 显示规则归属到点对象自身，边对象只负责声明角色变化。
     */
    public attachEndpointRole(): void {
        this.endpointRoleReferenceCount += 1;
        this.isEndPoint = true;
        this.visible = true;
    }

    /**
     * 解绑端点角色并按当前拓扑角色回退显示状态。
     * 说明：
     * 1. 仅当端点引用计数归零时才会真正清除端点角色；
     * 2. 若该点仍是中点，则恢复默认隐藏；
     * 3. 若该点不是中点，则保持当前显示状态不变，避免误伤普通控制点。
     */
    public detachEndpointRole(): void {
        this.endpointRoleReferenceCount = Math.max(0, this.endpointRoleReferenceCount - 1);
        if (this.endpointRoleReferenceCount > 0) {
            this.isEndPoint = true;
            this.visible = true;
            return;
        }
        this.isEndPoint = false;
        this.refreshDefaultVisibilityByTopologyRole();
    }

    /**
     * 绑定中点角色并按中点默认规则收口显示状态。
     * 说明：
     * 1. 默认中点在未被捕捉或未复用为端点时应保持隐藏；
     * 2. 若当前点已经作为端点使用，则不应因为新增中点角色而被隐藏；
     * 3. 中点移入高亮等临时显示逻辑仍可在外层直接改 visible。
     */
    public attachMidPointRole(): void {
        this.isMidPoint = true;
        this.refreshDefaultVisibilityByTopologyRole();
    }

    /**
     * 绑定圆、圆弧或椭圆中心控制点角色，并应用统一十字材质。
     *
     * 该角色与通用 `isCenterPoint` 分离，避免把抛物线焦点等其他中心语义错误改成十字。
     */
    public attachCurveCenterControlPointRole(): void {
        this.isCenterPoint = true;
        this.isCurveCenterControlPoint = true;
        this.material = PointsMaterialFactory.getCurveCenterPointSpriteMaterial();
    }

    /**
     * 解除圆、圆弧或椭圆中心控制点角色，并恢复普通控制点材质。
     */
    public detachCurveCenterControlPointRole(): void {
        this.isCenterPoint = false;
        this.isCurveCenterControlPoint = false;
        this.material = PointsMaterialFactory.getSketchControlPointSpriteMaterial();
    }


    /**
     * 判断当前点是否为椭圆长短轴控制点。
     * 说明：
     * 1. 椭圆轴控制点虽然复用象限点集合参与捕捉，但它们承担半轴编辑职责；
     * 2. 中心点不属于轴端控制点，应继续走中心点自身可见性规则；
     * 3. 该方法供交互默认态和历史快照规范化复用，避免不同链路重复判断枚举项。
     * @returns 若当前点是椭圆长轴或短轴端控制点则返回 true。
     */
    public isEllipseAxisControlPoint(): boolean {
        return this.ellipseControlPointRole === EllipseControlPointRole.MajorPositive
            || this.ellipseControlPointRole === EllipseControlPointRole.MajorNegative
            || this.ellipseControlPointRole === EllipseControlPointRole.MinorPositive
            || this.ellipseControlPointRole === EllipseControlPointRole.MinorNegative;
    }

    /**
     * 按当前拓扑角色刷新点对象默认显示状态。
     * 说明：
     * 1. 端点优先级高于中点，端点必须可见；
     * 2. 椭圆轴控制点是可编辑控制点，不能被普通象限点临时显示规则隐藏；
     * 3. 纯中点和非椭圆轴向象限点默认隐藏；
     * 4. 其他点类型保持现状，不在这里强制修改。
     */
    public refreshDefaultVisibilityByTopologyRole(): void {
        if (this.isEndPoint) {
            this.visible = true;
            return;
        }
        if (this.isEllipseAxisControlPoint()) {
            this.visible = true;
            return;
        }
        if (this.isMidPoint || this.isQuadrantPoint) {
            this.visible = false;
        }
    }


    /**
     * 注册一个关联草图对象。
     * @param object 草图对象。
     */
    public registerRelatedSketchObject(object: SketchRelatedObject): void {
        this.relatedSketchObjects.add(object);
    }

    /**
     * 解除一个关联草图对象。
     * @param object 草图对象。
     */
    public unregisterRelatedSketchObject(object: SketchRelatedObject): void {
        this.relatedSketchObjects.delete(object);
    }

    /**
     * 获取当前点关联草图对象的只读快照。
     * 说明：
     * 1. 返回当前时刻关联对象集合副本；
     * 2. 调用方不可直接修改内部关联集合；
     * 3. 用于拖拽判定等只读场景。
     * @returns 关联草图对象数组快照。
     */
    public getRelatedSketchObjectsSnapshot(): readonly SketchRelatedObject[] {
        return [...this.relatedSketchObjects];
    }

    /**
     * 获取当前点关联边的只读快照。
     * 说明：
     * 1. 仅返回当前时刻的集合副本，调用方不可反向修改内部集合；
     * 2. 用于上层交互模块在拖拽点时向上解析关联线/线段几何对象；
     * 3. 结果顺序与集合迭代顺序一致，不承诺稳定排序语义。
     * @returns 关联边对象数组快照。
     */
    public getConnectedEdgesSnapshot(): readonly AbstractSketchEdgeObject[] {
        return [...this.connectedEdges];
    }



    /**
     * 通知所有关联边刷新自身几何
     */
    private notifyConnectedLines(): void {
        this.connectedEdges.forEach((edge) => edge.syncEndpointsToGeometry(this));
    }

    /**
     * 将点位直接写入点几何。
     * 说明：
     * 1. 该方法只负责底层几何写入；
     * 2. 调用方决定是否继续触发拓扑联动；
     * 3. 点对象缺少顶点属性时直接抛错，不做兜底。
     * @param position 目标点位。
     */
    private writePositionToGeometry(position: THREE.Vector3): void {
        // 统一消除反射矩阵产生的负零，避免等价坐标污染严格定义比较与历史快照。
        this.position.set(
            position.x === 0 ? 0 : position.x,
            position.y === 0 ? 0 : position.y,
            position.z === 0 ? 0 : position.z,
        )
    }
}
